import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

export default function AdminEditArticlePage() {
    const { articleId } = useParams();
    const navigate = useNavigate();
    const isNewArticle = !articleId || articleId === 'new';

    const [title, setTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [content, setContent] = useState('');
    const [isPublished, setIsPublished] = useState(true);
    const [loading, setLoading] = useState(!isNewArticle);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isNewArticle && articleId) {
            const fetchArticle = async () => {
                setLoading(true);
                try {
                    const docRef = doc(db, 'learning_articles', articleId);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setTitle(data.title || '');
                        setSlug(data.slug || '');
                        setContent(data.content || '');
                        setIsPublished(data.published === undefined ? true : data.published);
                    } else {
                        toast.error("Article not found.");
                        navigate('/admin/learning');
                    }
                } catch (error) {
                    toast.error("Failed to load article.");
                    console.error("Firestore fetch error:", error);
                }
                setLoading(false);
            };
            fetchArticle();
        }
    }, [articleId, isNewArticle, navigate]);
    
    const handleSave = async () => {
        if (!title || !slug || !content) {
            return toast.error('Title, Slug, and Content are required.');
        }
        
        setIsSaving(true);
        const toastId = toast.loading('Saving article...');

        const dataToSave = {
            title,
            slug,
            content,
            published: isPublished,
            updatedAt: serverTimestamp()
        };

        try {
            if (isNewArticle) {
                dataToSave.createdAt = serverTimestamp();
                await addDoc(collection(db, 'learning_articles'), dataToSave);
                toast.success('Article created successfully!', { id: toastId });
            } else {
                const docRef = doc(db, 'learning_articles', articleId);
                await setDoc(docRef, dataToSave, { merge: true });
                toast.success('Article updated successfully!', { id: toastId });
            }
            navigate('/admin/learning');
        } catch (error) {
            toast.error(`Failed to save: ${error.message}`, { id: toastId });
            console.error("Firestore save error:", error);
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="animate-spin text-cyan-400" size={48} />
            </div>
        );
    }

    return (
        <div className="glass-card rounded-2xl p-8 space-y-6">
            <h1 className="text-3xl font-bold text-white">{isNewArticle ? 'Create New Article' : 'Edit Article'}</h1>
            
            <div>
                <label className="block text-gray-300 mb-2 font-semibold">Title</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full glass-card rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" placeholder="Enter article title" />
            </div>
            
            <div>
                <label className="block text-gray-300 mb-2 font-semibold">URL Slug</label>
                <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))} className="w-full glass-card rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" placeholder="e.g., my-new-article-slug" />
            </div>

            <div>
                <label className="block text-gray-300 mb-2 font-semibold">Content</label>
                <div className="bg-slate-800 text-white rounded-lg quill-container">
                    <ReactQuill theme="snow" value={content} onChange={setContent} />
                </div>
            </div>
            
            <div className="flex items-center gap-4">
                <input type="checkbox" id="isPublished" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} className="h-4 w-4 rounded bg-gray-700 border-gray-600 text-cyan-500 focus:ring-cyan-500" />
                <label htmlFor="isPublished" className="text-gray-300">Published</label>
            </div>

            <button onClick={handleSave} disabled={isSaving} className="flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-bold py-2 px-6 rounded-lg disabled:opacity-50">
                {isSaving && <Loader2 className="animate-spin" size={20} />}
                {isSaving ? 'Saving...' : 'Save Article'}
            </button>
        </div>
    );
}

